import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Music, Download, Video, AlertCircle, ExternalLink, Play } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { tiktokService, API_LIMITATIONS } from '../services/socialMediaApi';

interface TikTokVideoData {
  videoId: string;
  username: string;
  description: string;
  duration: string;
  likes: number;
  comments: number;
  shares: number;
  plays: number;
  videoUrl: string;
  audioUrl: string;
  thumbnailUrl: string;
  createdAt: string;
}

const TikTokVideoDownloader: React.FC = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [videoData, setVideoData] = useState<TikTokVideoData | null>(null);
  const [downloadFormat, setDownloadFormat] = useState<'mp4' | 'mp3'>('mp4');

  useSEO({
    title: 'TikTok Video Downloader - Download TikTok Videos Without Watermark',
    description: 'Download TikTok videos without watermark in HD quality. Free TikTok downloader tool to save TikTok videos and audio.',
    keywords: 'tiktok video downloader, tiktok downloader no watermark, download tiktok videos, tiktok mp4 download',
    canonical: 'https://snap-tools.online/social/tiktok-video-downloader'
  });

  // Fetch video data
  const fetchVideoData = async () => {
    if (!videoUrl.trim()) {
      toast.error('Please enter a TikTok video URL');
      return;
    }

    const videoInfo = tiktokService.extractVideoInfo(videoUrl.trim());
    if (!videoInfo) {
      toast.error('Invalid TikTok URL. Please check the URL format.');
      return;
    }

    setIsLoading(true);
    try {
      // Show API limitation notice
      toast(API_LIMITATIONS.TIKTOK, { duration: 6000 });

      // Attempt to get video data (will throw error due to API requirements)
      await tiktokService.getVideoData(videoInfo.videoId);

    } catch (error) {
      console.error('Failed to fetch video data:', error);
      
      // Show demo data with API limitation notice
      const mockData: TikTokVideoData = {
        videoId: videoInfo.videoId,
        username: videoInfo.username,
        description: '[DEMO] TikTok video - API access required for real data #apiRequired #demo',
        duration: '0:15',
        likes: 15400,
        comments: 890,
        shares: 245,
        plays: 125600,
        videoUrl: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
        audioUrl: 'https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a',
        thumbnailUrl: 'https://picsum.photos/400/600?random=2',
        createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
      };

      setVideoData(mockData);
      toast.success('Demo data loaded. TikTok API required for real video access.');
    } finally {
      setIsLoading(false);
    }
  };

  // Download video/audio
  const downloadContent = async () => {
    if (!videoData) return;

    try {
      const url = downloadFormat === 'mp4' ? videoData.videoUrl : videoData.audioUrl;
      const extension = downloadFormat === 'mp4' ? 'mp4' : 'mp3';
      
      // Show download info
      toast(`Preparing ${downloadFormat.toUpperCase()} download...`);
      
      // Try direct download first
      try {
        await fetch(url, {
          mode: 'no-cors',
          headers: {
            'Accept': downloadFormat === 'mp4' ? 'video/*' : 'audio/*'
          }
        });
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `${videoData.username}_${videoData.videoId}_no_watermark.${extension}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`${downloadFormat.toUpperCase()} download started! File will be saved as: ${videoData.username}_${videoData.videoId}_no_watermark.${extension}`);
      } catch (fetchError) {
        // Fallback: Open in new tab
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.success(`${downloadFormat.toUpperCase()} opened in new tab. Right-click and select "Save ${downloadFormat === 'mp4' ? 'video' : 'audio'} as..." to download.`);
      }
      
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download content');
    }
  };

  // Format number
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Load example
  const loadExample = () => {
    setVideoUrl('https://www.tiktok.com/@username/video/1234567890123456789');
    toast.success('Example URL loaded');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full mb-4">
              <Music className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">TikTok Video Downloader</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Download TikTok videos without watermark in high quality. Save TikTok videos as MP4 or extract audio as MP3.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Input Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  TikTok Video URL
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.tiktok.com/@username/video/1234567890123456789"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && fetchVideoData()}
                  />
                  <button
                    onClick={loadExample}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Example
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Download Format
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setDownloadFormat('mp4')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      downloadFormat === 'mp4'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <Video className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">Video (MP4)</div>
                    <div className="text-xs text-gray-500">No watermark</div>
                  </button>
                  <button
                    onClick={() => setDownloadFormat('mp3')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      downloadFormat === 'mp3'
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    <Music className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-sm font-medium">Audio (MP3)</div>
                    <div className="text-xs text-gray-500">Audio only</div>
                  </button>
                </div>
              </div>

              <button
                onClick={fetchVideoData}
                disabled={isLoading || !videoUrl.trim()}
                className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Video className="w-4 h-4" />
                    Get Video
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Video Data */}
          {videoData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-gray-200 rounded-lg p-6 mb-8"
            >
              <div className="flex flex-col md:flex-row gap-6">
                {/* Video Preview */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img
                      src={videoData.thumbnailUrl}
                      alt="Video thumbnail"
                      className="w-48 h-64 object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg">
                      <Play className="w-12 h-12 text-white" />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {videoData.duration}
                    </div>
                  </div>
                </div>

                {/* Video Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">@{videoData.username}</h3>
                  </div>
                  
                  <p className="text-gray-700 mb-4">{videoData.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="text-center">
                      <div className="text-lg font-bold text-pink-600">{formatNumber(videoData.likes)}</div>
                      <div className="text-sm text-gray-600">Likes</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-blue-600">{formatNumber(videoData.comments)}</div>
                      <div className="text-sm text-gray-600">Comments</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">{formatNumber(videoData.shares)}</div>
                      <div className="text-sm text-gray-600">Shares</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-600">{formatNumber(videoData.plays)}</div>
                      <div className="text-sm text-gray-600">Plays</div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-500 mb-6">
                    Posted: {formatDate(videoData.createdAt)}
                  </div>

                  {/* Download Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={downloadContent}
                      className="flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download {downloadFormat.toUpperCase()}
                      {downloadFormat === 'mp4' && <span className="text-xs">(No Watermark)</span>}
                    </button>
                    <button
                      onClick={() => window.open(videoUrl, '_blank')}
                      className="flex items-center justify-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Original
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 mb-1">Important Notice</h4>
                <p className="text-sm text-yellow-800">
                  This tool is for personal use only. Please respect content creators' rights and TikTok's terms of service. 
                  Always obtain permission before using downloaded content commercially or redistributing it.
                </p>
              </div>
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">How to Use</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Copy the TikTok video URL from the app or web</li>
                <li>• Paste it into the input field above</li>
                <li>• Choose between video (MP4) or audio (MP3) format</li>
                <li>• Click "Get Video" to load the content</li>
                <li>• Click "Download" to save without watermark</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Features</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Download videos without watermark</li>
                <li>• Extract audio as MP3 files</li>
                <li>• High-quality video downloads</li>
                <li>• View video statistics and info</li>
                <li>• Fast and secure processing</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Supported URLs</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• Standard TikTok URLs (tiktok.com/@user/video/)</li>
                <li>• Short URLs (vm.tiktok.com/)</li>
                <li>• Share URLs (tiktok.com/t/)</li>
                <li>• Mobile app shared links</li>
                <li>• All public TikTok videos</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 mb-3">Legal Guidelines</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• Respect content creators' intellectual property</li>
                <li>• Use downloads for personal purposes only</li>
                <li>• Don't redistribute copyrighted content</li>
                <li>• Follow TikTok's community guidelines</li>
                <li>• Always credit original creators when sharing</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TikTokVideoDownloader;
